/**
 * Notifications Page
 *
 * View and manage in-app notifications:
 * - Budget alerts, bill reminders, goal milestones
 * - Anomaly detection alerts
 * - Mark as read, delete, clear all read
 * - Unread count indicator
 */
import { useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  clearReadNotifications,
  fetchUnreadCount,
} from '@/features/notifications/notificationsSlice';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  AlertTriangle,
  Target,
  Wallet,
  Calendar,
  Info,
  Zap,
  RefreshCw,
} from 'lucide-react';
import PageGuide from '@/components/common/PageGuide';
import { notificationsGuide } from '@/components/common/guideContent';

const typeIcons: Record<string, typeof Bell> = {
  budget_alert: Wallet,
  bill_reminder: Calendar,
  goal_milestone: Target,
  anomaly: AlertTriangle,
  subscription_change: RefreshCw,
  system: Info,
  sweep_suggestion: Zap,
};

const priorityColors: Record<string, string> = {
  low: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  medium: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  urgent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export default function Notifications() {
  useDocumentTitle('Notifications');
  const dispatch = useAppDispatch();
  const { notifications, unreadCount, isLoading } = useAppSelector((state) => state.notifications);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(fetchNotifications(undefined));
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  return (
    <div className="space-y-6">
      <PageGuide pageKey="notifications" content={notificationsGuide} />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Notifications
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2 text-xs">
                {unreadCount}
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground">Stay informed about your financial activity</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => dispatch(markAllNotificationsRead())}
            disabled={unreadCount === 0}
          >
            <CheckCheck className="mr-2 h-4 w-4" /> Mark All Read
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => dispatch(clearReadNotifications())}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Clear Read
          </Button>
        </div>
      </div>

      {/* Notifications List */}
      <Card>
        <CardHeader>
          <CardTitle>All Notifications</CardTitle>
          <CardDescription>
            {notifications.length} total &middot; {unreadCount} unread
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No notifications</p>
              <p className="text-sm">You're all caught up!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((notification) => {
                const IconComponent = typeIcons[notification.type] || Bell;
                return (
                  <div
                    key={notification._id}
                    className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                      notification.read ? 'opacity-60' : 'bg-accent/30'
                    }`}
                  >
                    <div className="mt-0.5">
                      <IconComponent className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className={`text-sm font-medium ${notification.read ? '' : 'font-semibold'}`}>
                          {notification.title}
                        </p>
                        <Badge className={priorityColors[notification.priority] || ''} variant="outline">
                          {notification.priority}
                        </Badge>
                        {!notification.read && (
                          <div className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground break-words">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(notification.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => dispatch(markNotificationRead(notification._id))}
                          title="Mark as read"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => dispatch(deleteNotification(notification._id))}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
